import * as React from 'react';
import clsx from 'clsx';
import { IEntityTypes, ReferralCampaign } from '@poinz/api';
import useApi from 'hooks/useApi';
import * as Sentry from '@sentry/nextjs';
import { useHasCreditCard } from 'containers/UserAreaCashback/BalanceAndActivity/Balance/Balance.utils';
import { useAuth } from 'contexts/authUser/store';
import { authUserSelectors } from 'contexts/authUser';
import { Sparkles } from 'lucide-react';
import Paragraph from 'components/text/Paragraph';
import { useTranslation } from 'next-i18next';
import Heading from 'components/text/Heading';
import Button from 'components/Button';
import Image from 'next/image';
import { scaledImageLoader } from 'utils/image';

interface Props {
  entityType: IEntityTypes;
  entityId?: number;
  className?: string;
  imageSrc: string;
}

const CreditCardReferralPromotion: React.FC<Props> = ({
  entityType,
  entityId,
  className,
  imageSrc
}) => {
  const { api } = useApi();
  const [campaign, setCampaign] = React.useState<ReferralCampaign | null>(null);
  const hasCreditCard = useHasCreditCard();
  const { isLoggedIn, loading } = useAuth(state => ({
    isLoggedIn: authUserSelectors.isLoggedIn(state),
    loading: state.loading
  }));
  const { t } = useTranslation();

  const link = campaign?.links?.[0]?.link;

  React.useEffect(() => {
    if (entityType && entityId && !!api) {
      (async () => {
        try {
          const camp = await api.referral.campaign.getPublicCampaign({ entityId, entityType });
          setCampaign(camp);
        } catch (e: any) {
          if (e.status !== 404) {
            Sentry.captureException(e);
          }
        }
      })();
    }
  }, [entityType, entityId, api]);

  if (loading || (isLoggedIn && hasCreditCard) || !link) {
    return null;
  }

  return (
    <div
      className={clsx(
        'CreditCardReferralPromotion flex flex-col sm:flex-row justify-between px-6 sm:px-8 py-[30px] sm:py-9 gap-8 rounded-3xl bg-primary-lightest items-center',
        className
      )}
    >
      <div className="w-fit sm:max-w-[calc(100%-320px)]">
        {/* orange badge */}
        <div className="flex rounded-full w-fit px-3 py-[6px] items-center justify-center gap-[6px] bg-primary-lighter">
          <Sparkles size={16} className="text-primary" />
          <Paragraph variant="caption" color="main" className="font-medium">
            {t('shop.giftCards.forYou')}
          </Paragraph>
        </div>
        <Heading variant="h4" className="text-primary mt-4">
          {t('shop.giftCards.waitAMinute')}
        </Heading>
        <Paragraph variant="body1" className="text-primary mt-1">
          {t('shop.giftCards.applyForTheFreePoinzCreditCards')}
        </Paragraph>
        <Button variant="tertiary" href={link} className="mt-6 !bg-transparent">
          {t('shop.giftCards.applyForCards')}
        </Button>
      </div>
      <div className="relative w-full max-w-[277px] self-baseline sm:self-auto">
        <Image
          src={imageSrc}
          width={100}
          height={100 * (9 / 16)}
          loader={scaledImageLoader()}
          alt="credit card image"
          className="aspect-[16/10] absolute rounded-[4px] top-1/2 right-0 sm:left-0 -translate-y-1/2 shadow-poinz-card object-cover"
        />
        <div className="flex gap-2 items-center justify-start sm:justify-end">
          <Image
            src="/assets/top-credit-card.webp"
            width={58}
            height={58}
            loader={scaledImageLoader()}
            alt="credit card image"
            className="absolute top-0 -right-[25px] -translate-y-1/2 hidden sm:block"
          />
          <Image
            src="/assets/amex.webp"
            alt={'American Express Poinz Loyalty Card'}
            width={100}
            className="shadow-poinz-card"
            loader={scaledImageLoader()}
            height={(427 / 270) * 100}
          />
          <Image
            src="/assets/visa.webp"
            alt={'American Express Poinz Loyalty Card'}
            width={100}
            className="shadow-poinz-card"
            loader={scaledImageLoader()}
            height={(427 / 270) * 100}
          />
        </div>
      </div>
    </div>
  );
};

export default CreditCardReferralPromotion;
